import { useEffect, useRef, useState } from 'react';
import { App, Button, Checkbox, DatePicker, Flex, Input, Select, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { formatStock } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { IntInput, MoneyInput } from '@/shared/ui/inputs';
import { CategorySearchSelect } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';
import { CounterpartySelect } from '@/entities/counterparty';
import {
  useCancelDocumentMutation,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useDocumentPaymentsQuery,
  useDuplicateTemplateQuery,
  useGetDocumentQuery,
  usePostDocumentMutation,
  useReturnDocumentToDraftMutation,
  useReturnTemplateQuery,
  useUnpostDocumentMutation,
  useUpdateDocumentMutation,
} from '@/entities/document';
import type { PaymentMethod } from '@/entities/payment';
import type { WriteOffReason } from '@/entities/document';
import { BatchSelect } from '@/entities/stock';
import { AttachmentsBlock } from '@/features/attachments';
import { PreviewConsequencesDialog, useSaveWithPreview } from '@/features/document/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { UnsavedChangesDialog } from '@/shared/ui';
import { useWindowManager } from '@/shared/lib/windows';
import { useStoreContext } from '@/shared/lib/store-context';
import { ownerPath } from '@/shared/config/routes';
import { todayInTz } from '@/shared/lib/date';
import { translateApiError } from '@/shared/api/errorToMessage';
import type { EditorDocumentType } from './model';
import {
  amountOf,
  applyCategoryDefaults,
  emptyLine,
  fillFromStock,
  adoptLineIds,
  fromDocumentLine,
  fromDuplicateTemplate,
  fromReturnTemplate,
  inventoryDiff,
  isDocumentComplete,
  qtyOf,
  recalcMarkup,
  toLineInput,
  totals,
  windowTitle,
} from './model';
import type { EditorLine } from './model';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };
const METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Наличные' },
  { value: 'CASHLESS', label: 'Безналичные' },
  { value: 'TRANSFER', label: 'Перевод' },
];
const WRITE_OFF_REASON_OPTIONS: { value: WriteOffReason; label: string }[] = [
  { value: 'SPOILAGE', label: 'Порча' },
  { value: 'EXPIRED', label: 'Истёк срок' },
  { value: 'SHORTAGE', label: 'Недостача' },
  { value: 'DEFECT', label: 'Брак' },
  { value: 'OTHER', label: 'Другое' },
];
/** Есть ли у этого типа осмысленный paidAmount/paymentStatus (§7) — только PURCHASE/SALE
 * являются целями FIFO-разнесения; RETURN_IN/RETURN_OUT — только источники кредита, WRITE_OFF
 * вообще без контрагента, их paidAmount/paymentStatus в БД никогда не обновляются. */
function hasPaymentTracking(type: EditorDocumentType): boolean {
  return type === 'PURCHASE' || type === 'SALE';
}

/** §11.2/§11.3: подпись поля «Оплачено» для возвратов — отдельная сумма деньгами сверх
 * автоматического кредита по основанию, создаёт REFUND при проведении. */
function refundLabel(type: EditorDocumentType): string | null {
  if (type === 'RETURN_IN') return 'Возвращено деньгами';
  if (type === 'RETURN_OUT') return 'Получено деньгами';
  return null;
}

export interface DocumentWindowProps {
  type: EditorDocumentType;
  id?: string;
  /** §11.2/§11.3 «Создать возврат»: id проведённой продажи/прихода-основания для нового документа. */
  returnTemplateFrom?: string;
  /** §12.6 «Дублировать»: id документа-источника для нового документа. */
  copyOf?: string;
  /** Номер документа-источника для заголовка «Новый приход (копия 125/2026)». */
  copyOfLabel?: string;
  onClose: () => void;
}

export function DocumentWindow({ type, id, returnTemplateFrom, copyOf, copyOfLabel, onClose }: DocumentWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const [documentId, setDocumentId] = useState(id);
  const gridApiRef = useRef<{ focusCell: (row: number, col: number) => void } | null>(null);

  const { data: doc, isFetching, refetch: refetchDoc } = useGetDocumentQuery(documentId ?? '', { skip: !documentId });
  const [createDocument, { isLoading: isCreating }] = useCreateDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();
  const [postDocument, { isLoading: isPosting }] = usePostDocumentMutation();
  const [unpostDocument, { isLoading: isUnposting }] = useUnpostDocumentMutation();
  const [cancelDocument, { isLoading: isCancelling }] = useCancelDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [returnToDraft] = useReturnDocumentToDraftMutation();
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = useSaveWithPreview();
  const windows = useWindowManager();

  const [header, setHeader] = useState<{
    docDate: string;
    counterpartyId?: string;
    comment: string;
    draftPaidAmount: string | null;
    draftPaidMethod: PaymentMethod;
    baseDocumentId?: string;
  }>({
    docDate: todayInTz(ctx.timezone),
    comment: '',
    draftPaidAmount: null,
    draftPaidMethod: 'CASH',
  });
  const [lines, setLines] = useState<EditorLine[]>([emptyLine(1)]);
  const [dirty, setDirty] = useState(false);
  // Пока false — «Оплачено» автоподстраивается под сумму нового прихода (см. эффект ниже);
  // становится true как только загружен реальный документ или пользователь тронул поле руками.
  const [paidAmountTouched, setPaidAmountTouched] = useState(false);
  function seedFrom(d: NonNullable<typeof doc>) {
    setHeader({
      docDate: d.docDate.slice(0, 10),
      counterpartyId: d.counterpartyId ?? undefined,
      comment: d.comment ?? '',
      draftPaidAmount: d.draftPaidAmount,
      draftPaidMethod: (d.draftPaidMethod as PaymentMethod | null) ?? 'CASH',
      baseDocumentId: d.baseDocumentId ?? undefined,
    });
    setLines(
      d.lines.length > 0
        ? [...d.lines.map(fromDocumentLine), ...(d.status === 'CANCELLED' ? [] : [emptyLine(d.lines.length + 1)])]
        : [emptyLine(1)],
    );
    setDirty(false);
    // Загруженный документ — «Оплачено» уже осмысленное сохранённое значение, автоподстановкой
    // суммы документа (см. ниже) больше не трогаем.
    setPaidAmountTouched(true);
  }

  // Loaded doc → seed the editor once (render-phase, not an effect — see React docs on adjusting
  // state when a prop changes). Keyed by id since a POSTED document re-fetches after every action.
  const [seededId, setSeededId] = useState<string | undefined>(undefined);
  if (doc && doc.id !== seededId) {
    setSeededId(doc.id);
    seedFrom(doc);
  }

  /** §8.7 «Параллельная правка»: VERSION_CONFLICT предлагает открыть свежую версию (отбрасывая
   * локальные правки) вместо тупикового тоста; любая другая ошибка идёт через обычный toast. */
  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Документ изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      onOk: async () => {
        const result = await refetchDoc();
        if (result.data) seedFrom(result.data);
      },
    });
  }

  // §11.2/§11.3 «Создать возврат»: новый документ (без id) открытый с основанием — подтягиваем
  // шаблон один раз, тем же render-phase-seed паттерном, что и для загруженного doc выше.
  const { data: returnTemplate } = useReturnTemplateQuery(returnTemplateFrom ?? '', { skip: !returnTemplateFrom || !!id });
  const [seededTemplateFrom, setSeededTemplateFrom] = useState<string | undefined>(undefined);
  if (returnTemplate && returnTemplateFrom && returnTemplateFrom !== seededTemplateFrom) {
    setSeededTemplateFrom(returnTemplateFrom);
    setHeader((h) => ({ ...h, counterpartyId: returnTemplate.counterpartyId ?? undefined, baseDocumentId: returnTemplate.baseDocumentId }));
    setLines(fromReturnTemplate(returnTemplate));
  }

  // §12.6 «Дублировать»: новый документ (без id) открытый с источником — тот же паттерн.
  const { data: duplicateTemplate } = useDuplicateTemplateQuery(copyOf ?? '', { skip: !copyOf || !!id });
  const [seededCopyOf, setSeededCopyOf] = useState<string | undefined>(undefined);
  if (duplicateTemplate && copyOf && copyOf !== seededCopyOf) {
    setSeededCopyOf(copyOf);
    setHeader((h) => ({ ...h, counterpartyId: duplicateTemplate.counterpartyId ?? undefined, comment: duplicateTemplate.comment ?? '' }));
    setLines(fromDuplicateTemplate(duplicateTemplate));
  }

  const status = doc?.status ?? 'DRAFT';
  const isNew = !documentId;
  // §8.3/§8.7: проведённый документ теперь тоже редактируется («Сохранить» — перепроведение с
  // предпросмотром последствий по §8.5); только отменённый остаётся полностью read-only (только
  // «Вернуть в черновик»/«Дублировать»/«Закрыть», см. renderFooter).
  const isReadOnly = status === 'CANCELLED';
  const busy = isCreating || isUpdating || isPosting || isUnposting || isCancelling || isDeleting || isRunningPreview;
  const { data: documentPayments } = useDocumentPaymentsQuery(documentId ?? '', {
    skip: !documentId || status !== 'POSTED' || (!hasPaymentTracking(type) && refundLabel(type) === null),
  });

  function updateLine(key: string, patch: Partial<EditorLine>) {
    setDirty(true);
    setLines((prev) => prev.map((l) => (l.key === key ? recalcMarkup({ ...l, ...patch }) : l)));
  }

  function addRow() {
    setDirty(true);
    setLines((prev) => [...prev, emptyLine(prev.length + 1)]);
  }

  /** Выбор товара в строке — как `updateLine`, но если это была последняя (ранее пустая) строка,
   *  сразу добавляет новую пустую строку снизу, чтобы можно было продолжить без Enter/кнопки. */
  function selectCategory(line: EditorLine, rowIndex: number, category: CategoryDto) {
    setDirty(true);
    setLines((prev) => {
      const next = prev.map((l) => (l.key === line.key ? recalcMarkup(applyCategoryDefaults(l, category, type)) : l));
      if (rowIndex === prev.length - 1 && !line.categoryId) {
        return [...next, emptyLine(next.length + 1)];
      }
      return next;
    });
  }

  function removeRow(key: string) {
    setDirty(true);
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key).map((l, i) => ({ ...l, lineNo: i + 1 }))));
  }

  function handleFillFromStock() {
    setDirty(true);
    setLines((prev) => fillFromStock(prev));
  }

  function buildPayload() {
    return {
      docDate: header.docDate,
      counterpartyId: header.counterpartyId,
      comment: header.comment || undefined,
      // Явный '0.00', а не omit: PUT/document-на-проведении не обнуляют поле, если оно опущено
      // (Prisma «оставить как есть» для undefined) — а очистка «Оплачено» должна снимать intent.
      draftPaidAmount: header.draftPaidAmount ?? '0.00',
      draftPaidMethod: header.draftPaidMethod,
      lines: lines.filter((l) => l.categoryId).map((l) => toLineInput(l, type)),
    };
  }

  async function ensureCreated(): Promise<string> {
    if (documentId) return documentId;
    const created = await createDocument({ type, baseDocumentId: header.baseDocumentId, ...buildPayload() }).unwrap();
    setLines((prev) => adoptLineIds(prev, created.lines));
    setDocumentId(created.id);
    setDirty(false);
    return created.id;
  }

  async function handleSaveDraft(close: boolean) {
    try {
      if (!documentId) {
        await ensureCreated();
      } else {
        const updated = await updateDocument({ id: documentId, rowVersion: doc!.rowVersion, body: buildPayload() }).unwrap();
        setLines((prev) => adoptLineIds(prev, updated.lines));
        setDirty(false);
      }
      message.success('Черновик сохранён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  async function handlePost(close: boolean) {
    try {
      const currentId = await ensureCreated();
      const rowVersion = documentId ? (doc?.rowVersion ?? 1) : 1;
      const result = await postDocument({ id: currentId, body: { rowVersion, document: documentId ? buildPayload() : undefined } }).unwrap();
      setLines((prev) => adoptLineIds(prev, result.document.lines));
      setDirty(false);
      if (result.warnings.length > 0) {
        message.warning(`Проведено с предупреждениями: ${result.warnings.map((w) => w.code).join(', ')}`);
      } else {
        message.success('Документ проведён');
      }
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** §8.3/§8.5/§8.7: «Сохранить» проведённого документа — перепроведение с правкой строк/шапки,
   * с предпросмотром последствий перед сохранением. */
  async function handleSaveRepost(close: boolean) {
    if (!documentId || !doc) return;
    const payload = buildPayload();
    let warnings: { code: string }[] = [];
    try {
      const saved = await runWithPreview(
        documentId,
        'post',
        doc.rowVersion,
        async (stateVersion) => {
          const result = await postDocument({ id: documentId, body: { rowVersion: doc.rowVersion, document: payload, stateVersion } }).unwrap();
          warnings = result.warnings;
          // Сидируем локальный `lines`/`header` напрямую из ответа мутации — она гарантированно
          // свежая (перепроведённая формой на сервере). Полагаться на refetch `useGetDocumentQuery`
          // + сброс `seededId` не работает: между вызовом и завершением refetch случается лишний
          // рендер со старым закэшированным `doc` (тот же id), сид-гейт срабатывает на нём первым
          // и «съедает» будущее срабатывание для по-настоящему свежих данных с тем же id.
          seedFrom(result.document);
        },
        payload,
      );
      if (saved) {
        if (warnings.length > 0) {
          message.warning(`Перепроведено с предупреждениями: ${warnings.map((w) => w.code).join(', ')}`);
        } else {
          message.success('Документ перепроведён');
        }
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** §8.5: снятие проведения/отмена/удаление проведённого документа — предпросмотр последствий
   * перед сохранением (не блокирует, только предупреждает). */
  async function handleUnpost() {
    if (!documentId || !doc) return;
    try {
      const saved = await runWithPreview(documentId, 'unpost', doc.rowVersion, (stateVersion) =>
        unpostDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
      );
      if (saved) message.success('Проведение снято');
    } catch (e) {
      handleMutationError(e);
    }
  }

  function handleCancel() {
    if (!documentId || !doc) return;
    modal.confirm({
      title: 'Отменить документ?',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'cancel', doc.rowVersion, (stateVersion) =>
            cancelDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Документ отменён');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleDelete() {
    if (!documentId || !doc) return;
    modal.confirm({
      title: 'Удалить документ?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const saved =
            status === 'POSTED'
              ? await runWithPreview(documentId, 'remove', doc.rowVersion, (stateVersion) =>
                  deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
                )
              : await deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion } })
                  .unwrap()
                  .then(() => true);
          if (saved) {
            message.success('Документ удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  async function handleReturnToDraft() {
    if (!documentId || !doc) return;
    await returnToDraft({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
  }

  const t = totals(lines);
  const canPost = isDocumentComplete(type, header, lines);

  // Новый приход/продажа: пока пользователь не тронул «Оплачено» сам, поле следует за суммой
  // документа — render-phase adjustment (тот же паттерн, что и сид загруженного doc выше), не эффект.
  if (hasPaymentTracking(type) && status === 'DRAFT' && !paidAmountTouched && t.amount !== '0.00' && header.draftPaidAmount !== t.amount) {
    setHeader((h) => ({ ...h, draftPaidAmount: t.amount }));
  }

  // §1.8/§8.7: предупреждение о несохранённых изменениях не только при закрытии окна внутри
  // приложения, но и при закрытии вкладки/перезагрузке браузера целиком.
  useEffect(() => {
    if (!dirty) return;
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  /** §8.7: «Закрытие с несохранёнными изменениями» — Сохранить(-черновик) / Не сохранять / Отмена. */
  function handleClose() {
    if (!dirty) {
      onClose();
      return;
    }
    setShowUnsavedDialog(true);
  }

  async function handleSaveAndClose() {
    setShowUnsavedDialog(false);
    if (status === 'DRAFT') {
      await handleSaveDraft(true);
    } else {
      await handleSaveRepost(true);
    }
  }

  const columns: LinesGridColumn<EditorLine>[] = buildColumns(type, updateLine, selectCategory, isReadOnly);

  return (
    <>
      <PreviewConsequencesDialog diff={pendingDiff} onResolve={resolvePending} />
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        saveLabel={status === 'DRAFT' ? 'Сохранить черновик' : 'Сохранить'}
        saving={busy}
        onSave={() => void handleSaveAndClose()}
        onDiscard={() => {
          setShowUnsavedDialog(false);
          onClose();
        }}
        onCancel={() => setShowUnsavedDialog(false)}
      />
      <FloatingWindow kind={`document:${type}`} title={windowTitle(type, doc, isNew, isNew ? copyOfLabel : undefined)} onClose={handleClose} footer={renderFooter()}>
      <Flex vertical gap={8}>
        <Flex gap={8} wrap>
          <Flex vertical style={{ minWidth: 140 }}>
            <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
            <DatePicker
              value={dayjs(header.docDate)}
              format="DD.MM.YYYY"
              disabled={isReadOnly}
              onChange={(d) => {
                setDirty(true);
                setHeader((h) => ({ ...h, docDate: d ? d.format('YYYY-MM-DD') : h.docDate }));
              }}
            />
          </Flex>
          <Flex vertical style={{ minWidth: 220, flex: 1 }}>
            <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
            <CounterpartySelect
              value={header.counterpartyId}
              disabled={isReadOnly}
              onChange={(v) => {
                setDirty(true);
                setHeader((h) => ({ ...h, counterpartyId: v }));
              }}
            />
          </Flex>
          <Flex vertical style={{ minWidth: 200, flex: 2 }}>
            <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
            <Input
              value={header.comment}
              disabled={isReadOnly}
              onChange={(e) => {
                setDirty(true);
                setHeader((h) => ({ ...h, comment: e.target.value }));
              }}
            />
          </Flex>
        </Flex>

        {doc?.recalcPending ? <Tag color="warning">Пересчитывается — данные могут быть неактуальны</Tag> : null}

        {type === 'INVENTORY' && !isReadOnly ? (
          <Flex>
            <Button size="small" onClick={handleFillFromStock}>
              Заполнить по остаткам
            </Button>
          </Flex>
        ) : null}

        <LinesGrid
          lines={lines}
          columns={columns}
          getRowKey={(l) => l.key}
          onAddRow={isReadOnly ? () => undefined : addRow}
          onRemoveRow={isReadOnly ? () => undefined : removeRow}
          readOnly={isReadOnly}
          gridApiRef={gridApiRef}
        />

        <Flex gap={16}>
          <Typography.Text style={{ fontSize: 12 }}>Позиций: {t.positions}</Typography.Text>
          <Typography.Text style={{ fontSize: 12 }}>Штук: {t.pcs}</Typography.Text>
          <Typography.Text style={{ fontSize: 12 }} strong>
            Сумма: {t.amount}
          </Typography.Text>
        </Flex>

        {renderPaymentBlock()}

        <AttachmentsBlock documentId={documentId} readOnly={isReadOnly} />
      </Flex>
      </FloatingWindow>
    </>
  );

  function renderPaymentBlock() {
    const refund = refundLabel(type);
    if (!hasPaymentTracking(type) && !refund) return null;
    if (status === 'DRAFT') {
      return (
        <Flex gap={8} align="flex-end">
          <Flex vertical style={{ minWidth: 140 }}>
            <Typography.Text style={{ fontSize: 12 }}>{refund ?? 'Оплачено'}</Typography.Text>
            <MoneyInput
              value={header.draftPaidAmount}
              onChange={(v) => {
                setDirty(true);
                setPaidAmountTouched(true);
                setHeader((h) => ({ ...h, draftPaidAmount: v }));
              }}
            />
          </Flex>
          {header.draftPaidAmount ? (
            <Flex vertical style={{ minWidth: 160 }}>
              <Typography.Text style={{ fontSize: 12 }}>Способ</Typography.Text>
              <Select<PaymentMethod>
                value={header.draftPaidMethod}
                options={METHOD_OPTIONS}
                style={{ width: '100%' }}
                onChange={(v) => {
                  setDirty(true);
                  setHeader((h) => ({ ...h, draftPaidMethod: v }));
                }}
              />
            </Flex>
          ) : null}
        </Flex>
      );
    }
    if (status !== 'POSTED' || !documentId) return null;
    // §11.2/§11.3: возвраты не имеют paidAmount/paymentStatus (это не цели FIFO-разнесения, см.
    // hasPaymentTracking) — просто показываем сумму REFUND, если «возвращено/получено деньгами»
    // было указано при проведении.
    if (refund) {
      if (!documentPayments || documentPayments.length === 0) return null;
      return (
        <Flex vertical gap={2}>
          {documentPayments.map((p) => (
            <Typography.Text key={p.allocationId} style={{ fontSize: 12 }} type="secondary">
              {refund}: {p.amount}
              {p.payment ? ` · ${p.payment.method}` : ''}
            </Typography.Text>
          ))}
        </Flex>
      );
    }
    return (
      <Flex vertical gap={4}>
        <Flex gap={12} align="center">
          <Typography.Text style={{ fontSize: 12 }}>
            Оплачено: {doc?.paidAmount ?? '0.00'} из {doc?.totalAmount ?? '0.00'} ({PAYMENT_STATUS_LABEL[doc?.paymentStatus ?? 'UNPAID']})
          </Typography.Text>
          <Button
            size="small"
            onClick={() =>
              windows.open({
                kind: 'payment',
                counterpartyId: header.counterpartyId,
                prefillDocumentId: documentId,
                prefillAmount: doc ? String(Number(doc.totalAmount) - Number(doc.paidAmount)) : undefined,
                direction: type === 'SALE' ? 'IN' : 'OUT',
              })
            }
          >
            Добавить оплату
          </Button>
        </Flex>
        {documentPayments && documentPayments.length > 0 ? (
          <Flex vertical gap={2}>
            {documentPayments.map((p) =>
              p.payment ? (
                <Typography.Link
                  key={p.allocationId}
                  style={{ fontSize: 12 }}
                  onClick={() => windows.open({ kind: 'payment', id: p.payment!.id })}
                >
                  {p.payment.payDate.slice(0, 10)} · {p.amount} · {p.payment.method}
                </Typography.Link>
              ) : (
                <Typography.Text key={p.allocationId} style={{ fontSize: 12 }} type="secondary">
                  {p.amount} · возврат-кредит
                </Typography.Text>
              ),
            )}
            <Typography.Text style={{ fontSize: 12 }} type="secondary">
              Чтобы исправить сумму уже внесённой оплаты — откройте её по ссылке выше.
            </Typography.Text>
          </Flex>
        ) : null}
      </Flex>
    );
  }

  /** §12.6: «Дублировать» — открывает новое окно того же типа, предзаполненное из этого документа. */
  function handleDuplicate() {
    if (!documentId || !doc) return;
    windows.open({ kind: 'document', type, copyOf: documentId, copyOfLabel: `${doc.number}/${doc.numberYear}` });
  }

  function renderFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;

    if (status === 'DRAFT') {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canPost} onClick={() => void handlePost(true)}>
            Провести и закрыть
          </Button>
          <Button loading={busy} disabled={!canPost} onClick={() => void handlePost(false)}>
            Провести
          </Button>
          <Button loading={busy} onClick={() => void handleSaveDraft(false)}>
            Сохранить черновик
          </Button>
          <Button danger disabled={!documentId} loading={busy} onClick={() => void handleDelete()}>
            Удалить
          </Button>
          <Button disabled={!documentId} onClick={handleDuplicate}>
            Дублировать
          </Button>
          <Button onClick={handleClose}>Закрыть</Button>
        </Space>
      );
    }
    if (status === 'POSTED') {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canPost} onClick={() => void handleSaveRepost(true)}>
            Сохранить и закрыть
          </Button>
          <Button loading={busy} disabled={!canPost} onClick={() => void handleSaveRepost(false)}>
            Сохранить
          </Button>
          {(type === 'SALE' || type === 'PURCHASE') && documentId ? (
            <Button
              onClick={() => windows.open({ kind: 'document', type: type === 'SALE' ? 'RETURN_IN' : 'RETURN_OUT', returnTemplateFrom: documentId })}
            >
              Создать возврат
            </Button>
          ) : null}
          {documentId ? (
            <Button onClick={() => window.open(ownerPath(ctx.storeId, `/print/documents/${documentId}`), '_blank')}>Печать</Button>
          ) : null}
          <Button loading={busy} onClick={() => void handleUnpost()}>
            Снять проведение
          </Button>
          <Button loading={busy} onClick={() => void handleCancel()}>
            Отменить
          </Button>
          <Button danger loading={busy} onClick={() => void handleDelete()}>
            Удалить
          </Button>
          <Button onClick={handleDuplicate}>Дублировать</Button>
          <Button onClick={handleClose}>Закрыть</Button>
        </Space>
      );
    }
    // CANCELLED
    return (
      <Space wrap>
        <Button loading={busy} onClick={() => void handleReturnToDraft()}>
          Вернуть в черновик
        </Button>
        <Button onClick={handleDuplicate}>Дублировать</Button>
        <Button onClick={handleClose}>Закрыть</Button>
      </Space>
    );
  }
}

function buildColumns(
  type: EditorDocumentType,
  updateLine: (key: string, patch: Partial<EditorLine>) => void,
  selectCategory: (line: EditorLine, rowIndex: number, category: CategoryDto) => void,
  readOnly: boolean,
): LinesGridColumn<EditorLine>[] {
  const categoryCol: LinesGridColumn<EditorLine> = {
    key: 'category',
    title: 'Товар',
    width: 240,
    // §5 «Курсор встаёт в поле «Коробок»» после выбора товара — не реализовано в этом проходе
    // (борьба с ref-в-рендере line из react-hooks/refs не стоила усилий здесь); Tab всё ещё
    // работает нативно.
    render: (line, rowIndex) => (
      <CategorySearchSelect
        value={line.categoryId}
        disabled={readOnly}
        onChange={(_id, category) => selectCategory(line, rowIndex, category)}
      />
    ),
  };

  if (type === 'SALE') {
    return [
      categoryCol,
      {
        key: 'stock',
        title: 'Остаток',
        width: 140,
        render: (line) => <Typography.Text style={{ fontSize: 12 }}>{line.stock ? formatStock(line.stock) : '—'}</Typography.Text>,
      },
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
      {
        key: 'price',
        title: 'Цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'cost',
        title: 'Себестоимость',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.costAmount ?? '—'}{line.costIsProvisional ? ' (пред.)' : ''}</span>,
      },
    ];
  }

  if (type === 'PURCHASE') {
    return [
      categoryCol,
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
      {
        key: 'price',
        title: 'Цена за ед.',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v, lastEdited: line.lastEdited })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма закупки', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'wholesale',
        title: 'Оптовая цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput
            value={line.wholesalePrice}
            onChange={(v) => updateLine(line.key, { wholesalePrice: v, lastEdited: 'wholesale' })}
            navRow={rowIndex}
            navCol={4}
            disabled={readOnly}
          />
        ),
      },
      {
        key: 'markup',
        title: '%',
        width: 70,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput
            value={line.markupPct}
            onChange={(v) => updateLine(line.key, { markupPct: v, lastEdited: 'markup' })}
            navRow={rowIndex}
            navCol={5}
            disabled={readOnly}
          />
        ),
      },
      {
        key: 'isNew',
        title: 'Новинка',
        width: 70,
        align: 'center',
        render: (line) => <Checkbox checked={line.isNew} disabled={readOnly} onChange={(e) => updateLine(line.key, { isNew: e.target.checked })} />,
      },
      {
        key: 'isPromo',
        title: 'Акция',
        width: 60,
        align: 'center',
        render: (line) => <Checkbox checked={line.isPromo} disabled={readOnly} onChange={(e) => updateLine(line.key, { isPromo: e.target.checked })} />,
      },
      {
        key: 'expiry',
        title: 'Срок годности',
        width: 130,
        render: (line) => (
          <DatePicker
            value={line.expiryDate ? dayjs(line.expiryDate) : null}
            format="DD.MM.YYYY"
            disabled={readOnly}
            onChange={(d) => updateLine(line.key, { expiryDate: d ? d.format('YYYY-MM-DD') : null })}
          />
        ),
      },
    ];
  }

  if (type === 'RETURN_IN' || type === 'RETURN_OUT') {
    return [
      categoryCol,
      {
        key: 'base',
        title: type === 'RETURN_IN' ? 'Продано' : 'Куплено',
        width: 90,
        align: 'right',
        render: (line) => <span>{line.soldQty ?? '—'}</span>,
      },
      { key: 'returned', title: 'Уже возвращено', width: 110, align: 'right', render: (line) => <span>{line.alreadyReturnedQty ?? 0}</span> },
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
      {
        key: 'price',
        title: 'Цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'overBase',
        title: 'Сверх основания',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.qtyOverBase ? line.qtyOverBase : '—'}</span>,
      },
    ];
  }

  if (type === 'INVENTORY') {
    return [
      categoryCol,
      {
        key: 'accountedSealed',
        title: 'Учёт К',
        width: 80,
        align: 'right',
        render: (line) => <span>{line.stock ? line.stock.sealedBoxes : '—'}</span>,
      },
      {
        key: 'accountedLoose',
        title: 'Учёт Р',
        width: 80,
        align: 'right',
        render: (line) => <span>{line.stock ? line.stock.loosePcs : '—'}</span>,
      },
      {
        key: 'countedBoxes',
        title: 'Факт коробок',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.countedBoxes ?? null} onChange={(v) => updateLine(line.key, { countedBoxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'countedLoose',
        title: 'Факт россыпь',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.countedLoose ?? null} onChange={(v) => updateLine(line.key, { countedLoose: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      {
        key: 'diff',
        title: 'Разница, шт',
        width: 100,
        align: 'right',
        render: (line) => {
          const diff = inventoryDiff(line);
          if (diff === null) return <span>—</span>;
          return <span style={{ color: diff < 0 ? '#cf1322' : diff > 0 ? '#389e0d' : undefined }}>{diff > 0 ? `+${diff}` : diff}</span>;
        },
      },
    ];
  }

  // WRITE_OFF (§11.4): без контрагента и цены — «Сумма» появится после проведения (себестоимость).
  return [
    categoryCol,
    {
      key: 'stock',
      title: 'Остаток',
      width: 140,
      render: (line) => <Typography.Text style={{ fontSize: 12 }}>{line.stock ? formatStock(line.stock) : '—'}</Typography.Text>,
    },
    {
      key: 'boxes',
      title: 'Коробок',
      width: 80,
      align: 'right',
      render: (line, rowIndex) => (
        <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
      ),
    },
    {
      key: 'pcsPerBox',
      title: 'Штук в коробке',
      width: 100,
      align: 'right',
      render: (line, rowIndex) => (
        <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
      ),
    },
    { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
    {
      key: 'batch',
      title: 'Партия',
      width: 220,
      render: (line) => <BatchSelect categoryId={line.categoryId} value={line.batchId ?? undefined} disabled={readOnly} onChange={(v) => updateLine(line.key, { batchId: v ?? null })} />,
    },
    {
      key: 'reason',
      title: 'Причина',
      width: 140,
      render: (line) => (
        <Select<WriteOffReason>
          value={line.reason ?? undefined}
          disabled={readOnly}
          options={WRITE_OFF_REASON_OPTIONS}
          style={{ width: '100%' }}
          onChange={(v) => updateLine(line.key, { reason: v })}
        />
      ),
    },
    {
      key: 'cost',
      title: 'Себестоимость',
      width: 110,
      align: 'right',
      render: (line) => <span>{line.costAmount ?? '—'}</span>,
    },
  ];
}
